// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { getHeader } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import PanelMenu from "@insite/mobius/PanelMenu/PanelMenu";
import PanelRow from "@insite/mobius/PanelMenu/PanelRow";
import Typography from "@insite/mobius/Typography";
import { xGoogleAnalyticsState } from "@west/App/Store/Reducers";
import { NeedHelpComponent, NeedHelpComponentProps } from "@west/App/Widgets/Header/NeedHelp";
import { RowWrapper } from "@west/MobiusOverrides/PanelMenu";
import { NavigationDrawerStyles } from "@west/Overrides/Components/NavigationDrawer";
import React, { FC } from "react";

interface OwnProps {
    styles: NavigationDrawerStyles;
    currentPageUrl?: string;
    closeDrawer: () => void;
}

const NeedHelpNavigationDrawer: FC<OwnProps> = ({ styles, closeDrawer, currentPageUrl }) => {
    const { fields, xGoogleAnalytics } = useAppSelector(state => ({
        fields: getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
            return widget.type === "Header/NeedHelp";
        })?.fields as NeedHelpComponentProps["fields"],
        xGoogleAnalytics: (state as xGoogleAnalyticsState).xGoogleAnalytics,
    }));

    const destinationLink = useGetLink(fields.destination);

    let menu;
    let panelRow;
    const renderChildren = true;
    const isCurrentPage = currentPageUrl === destinationLink.url;

    let menuItem: JSX.Element | null = (
        <PanelRow
            href={destinationLink.url}
            className="main-navigation-row"
            onClick={closeDrawer}
            {...styles.mainNavigationRow}
            color="common.ui8"
        >
            <Typography className="main-navigation-row-text" {...styles.mainNavigationRowTypography}>
                {translate("Need Help?")}
            </Typography>
        </PanelRow>
    );

    if (renderChildren) {
        if (destinationLink.url) {
            panelRow = <PanelRow hasChildren isCurrent={isCurrentPage}></PanelRow>;
        } else {
            panelRow = React.cloneElement(menuItem, { hasChildren: true });
            menuItem = null;
        }
        menu = (
            <PanelMenu
                className="panel-menu"
                currentUrl={currentPageUrl}
                closeOverlay={closeDrawer}
                panelTrigger={panelRow}
                layer={0}
                // id used to open this drawer when clicking on needHelp header clickable
                id="NeedHelpNavigationDrawer"
                {...styles.panelMenu}
            >
                <NeedHelpComponent fields={fields} xGoogleAnalytics={xGoogleAnalytics} />
            </PanelMenu>
        );
    }

    if (!fields) {
        return null;
    }

    return (
        <RowWrapper isCurrent={isCurrentPage}>
            {menuItem}
            {menu}
        </RowWrapper>
    );
};

export default NeedHelpNavigationDrawer;
